import { useEffect } from 'react';
import * as PIXI from 'pixi.js';
import { redrawOccupiedTilesOverlay } from './render/occupiedOverlay';
import { drawTileGridOverlay, drawAdminTileOverlayImperative } from './render/overlays';
import type { AdminTileMode } from './useRoomActions';
import type { RoomPixiRoots } from './useRoomScene';
import type { RoomSceneState } from './state/roomSceneState';

// ── Grid/admin overlay + viewport housekeeping effects ──
// Extracted verbatim from RoomCanvas (decomposition final cleanup pass). Five
// small effects, registered in their original relative order (the hook is called
// right after useRoomScene, so registration order relative to co-dependent
// effects is unchanged — see the call-site comment in RoomCanvas):
//  * occupied-tiles overlay redraw on grid-mode change;
//  * tile grid overlay create/destroy on grid-mode change;
//  * admin tile selection overlay redraw;
//  * viewport re-centering when the scene becomes ready;
//  * viewport drag pause while the admin tile editor is open.
// The effect bodies are byte-identical to the originals except that the module
// scene state (roomViewport, roomContainer) is read through `scene` — the same
// live accessor object RoomCanvas passes to useRoomScene.

export interface UseRoomOverlaysParams {
  isSceneReady: boolean;
  mapObjectId: number;
  isCityHall: boolean;
  showGridMode: boolean;
  tileGridDebugEnabled: boolean;
  adminTileMode: AdminTileMode;
  adminSelectedTiles: Set<string>;
  FLOOR_HEIGHT: number;
  ROOM_WORLD_SIZE: number;
  scene: RoomPixiRoots;
  sceneRef: React.MutableRefObject<RoomSceneState>;
}

export function useRoomOverlays({
  isSceneReady,
  mapObjectId,
  isCityHall,
  showGridMode,
  tileGridDebugEnabled,
  adminTileMode,
  adminSelectedTiles,
  FLOOR_HEIGHT,
  ROOM_WORLD_SIZE,
  scene,
  sceneRef,
}: UseRoomOverlaysParams): void {
  // Redraw occupied tiles overlay when grid mode changes
  useEffect(() => {
    redrawOccupiedTilesOverlay(sceneRef.current.furniture.markers, sceneRef.current.overlays.occupiedTiles, sceneRef.current.flags.showGridMode);
  }, [showGridMode, sceneRef]);

  // Update grid overlay when grid mode changes
  useEffect(() => {
    const roomContainer = scene.roomContainer;
    if (!isSceneReady || !roomContainer) return;

    // Remove existing grid overlay if any
    if (sceneRef.current.overlays.tileGrid && roomContainer) {
      roomContainer.removeChild(sceneRef.current.overlays.tileGrid);
      sceneRef.current.overlays.tileGrid.destroy();
      sceneRef.current.overlays.tileGrid = null;
    }

    // Create new grid overlay if grid mode is enabled
    if (tileGridDebugEnabled && roomContainer) {
      const gridOverlay = new PIXI.Graphics();
      drawTileGridOverlay(gridOverlay);
      roomContainer.addChild(gridOverlay);
      sceneRef.current.overlays.tileGrid = gridOverlay;
    }
    // scene.roomContainer — live getter over module let; scene identity стабільний (module const).
  }, [tileGridDebugEnabled, isSceneReady, scene, sceneRef]);

  // Redraw admin tile selection overlay when selected tiles or mode changes
  useEffect(() => {
    drawAdminTileOverlayImperative(sceneRef.current.overlays.adminTile, adminTileMode, adminSelectedTiles, sceneRef.current.admin.tileCenter);
  }, [adminTileMode, adminSelectedTiles, isSceneReady, sceneRef]);

  // Center viewport on room center when scene becomes ready or when returning to room
  useEffect(() => {
    const roomViewport = scene.roomViewport;
    if (!isSceneReady || !roomViewport || !scene.roomContainer) return;

    // Calculate room center automatically from floor center
    // Floor is a diamond with center at (0, FLOOR_HEIGHT / 2) in room-local coords
    const roomCenterLocalX = 0; // Center of floor diamond horizontally
    const roomCenterLocalY = FLOOR_HEIGHT / 2; // Center of floor diamond vertically (250)

    // Convert to world coords by adding room container position
    const roomCenterWorldX = ROOM_WORLD_SIZE / 2 + roomCenterLocalX;
    const roomCenterWorldY = (ROOM_WORLD_SIZE / 2 - 100) + roomCenterLocalY;

    // Set zoom for "from high above" view - maximum zoom out
    // minScale = 0.3, maxScale = 3.0
    // Using minimum zoom (0.3) for the most distant view possible
    const targetZoom = 0.3;
    roomViewport.setZoom(targetZoom);

    // Center viewport on room center after setting zoom
    roomViewport.moveCenter(roomCenterWorldX, roomCenterWorldY);
  }, [isSceneReady, mapObjectId, isCityHall, FLOOR_HEIGHT, ROOM_WORLD_SIZE, scene]);

  // Pause viewport panning while admin tile editor is open so drag-paint doesn't also pan
  useEffect(() => {
    const roomViewport = scene.roomViewport;
    if (!roomViewport) return;
    if (adminTileMode !== null) {
      roomViewport.plugins.pause('drag');
    } else {
      roomViewport.plugins.resume('drag');
      // Clear any leftover drag state when editor closes
      sceneRef.current.admin.dragStartTile = null;
      const preview = sceneRef.current.overlays.adminDragPreview;
      if (preview) { preview.clear(); preview.visible = false; }
    }
  }, [adminTileMode, scene, sceneRef]);
}
